import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { MessageChannelSchema } from "../message/dto/ScheduleMessageAsStoreStaff"

export enum TireStorageQuantity {
  PAIR = "PAIR",
  SINGLE = "SINGLE",
}

export enum TireSeason {
  WINTER = "WINTER",
  SUMMER = "SUMMER",
  ALL_SEASON = "ALL_SEASON",
}

export enum TireStorageStatus {
  STORED = "STORED",
  RETURNED = "RETURNED",
  DISCARDED = "DISCARDED",
}

/** Where a stored tire is relative to its end date, worked out by the API. */
export enum TireStorageExpiry {
  OK = "OK",
  EXPIRING_SOON = "EXPIRING_SOON",
  EXPIRED = "EXPIRED",
}

export const TIRE_STORAGE_ID_PREFIX = "D-"

/** The ID mechanics write on the tires, e.g. "D-42". */
export const formatTireStorageId = (displayNumber: number): string =>
  `${TIRE_STORAGE_ID_PREFIX}${displayNumber}`

/** A tire ID as mechanics type it: "D-42", "d42" or "D 42". */
export const TIRE_STORAGE_ID_PATTERN = /^D-?\s*(\d+)$/i

/** The display number in a typed tire ID, also accepting a bare "42". */
export const parseTireStorageDisplayNumber = (
  search: string,
): number | null => {
  const trimmed = search.trim()
  const match =
    trimmed.match(TIRE_STORAGE_ID_PATTERN) ?? trimmed.match(/^(\d+)$/)
  if (!match) return null
  const parsed = Number(match[1])
  // Longer digit strings (e.g. phone numbers) don't fit the INT4 column.
  if (parsed > 2147483647) return null
  return parsed
}

export const TIRE_SEASON_LABEL_KEYS: Record<TireSeason, TranslationKey> = {
  [TireSeason.WINTER]: "common.tireStorage.season.winter",
  [TireSeason.SUMMER]: "common.tireStorage.season.summer",
  [TireSeason.ALL_SEASON]: "common.tireStorage.season.allSeason",
}

export const TIRE_STORAGE_QUANTITY_LABEL_KEYS: Record<
  TireStorageQuantity,
  TranslationKey
> = {
  [TireStorageQuantity.PAIR]: "common.tireStorage.quantity.pair",
  [TireStorageQuantity.SINGLE]: "common.tireStorage.quantity.single",
}

export const TIRE_STORAGE_STATUS_LABEL_KEYS: Record<
  TireStorageStatus,
  TranslationKey
> = {
  [TireStorageStatus.STORED]: "common.tireStorage.status.stored",
  [TireStorageStatus.RETURNED]: "common.tireStorage.status.returned",
  [TireStorageStatus.DISCARDED]: "common.tireStorage.status.discarded",
}

export interface TireStorageListItem {
  id: string
  displayNumber: number
  storeCustomerId: string
  customerName: string
  customerPhoneNumber: string
  storeBikeId: string
  bikeName: string | null
  quantity: TireStorageQuantity
  season: TireSeason
  description: string | null
  storageLocation: string | null
  note: string | null
  startDate: Date
  endDate: Date
  price: number
  isClubPrice: boolean
  status: TireStorageStatus
  expiry: TireStorageExpiry
  returnedAt: Date | null
  discardedAt: Date | null
  discardCustomerApprovedAt: Date | null
  discardRegisteredByName: string | null
}

const optionalText = z
  .string()
  .trim()
  .nullable()
  .transform((value) => (value === "" ? null : value))

export const CreateTireStorageSchema = z
  .object({
    storeCustomerId: z.string().uuid(),
    storeBikeId: z.string().uuid(),
    quantity: z.nativeEnum(TireStorageQuantity),
    season: z.nativeEnum(TireSeason),
    description: optionalText,
    storageLocation: optionalText,
    note: optionalText,
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    price: z.number().int().min(0),
  })
  .refine(({ startDate, endDate }) => endDate >= startDate, {
    message:
      "common.tireStorage.validation.endBeforeStart" satisfies TranslationKey,
    path: ["endDate"],
  })
export type CreateTireStorage = z.infer<typeof CreateTireStorageSchema>

export const UpdateTireStorageSchema = z
  .object({
    storeBikeId: z.string().uuid(),
    quantity: z.nativeEnum(TireStorageQuantity),
    season: z.nativeEnum(TireSeason),
    description: optionalText,
    storageLocation: optionalText,
    note: optionalText,
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    price: z.number().int().min(0),
  })
  .refine(({ startDate, endDate }) => endDate >= startDate, {
    message:
      "common.tireStorage.validation.endBeforeStart" satisfies TranslationKey,
    path: ["endDate"],
  })
export type UpdateTireStorage = z.infer<typeof UpdateTireStorageSchema>

export const DiscardTireStorageSchema = z.object({
  customerApproved: z.literal(true),
})
export type DiscardTireStorage = z.infer<typeof DiscardTireStorageSchema>

export enum TireStorageListFilter {
  STORED = "STORED",
  EXPIRING = "EXPIRING",
  RETURNED = "RETURNED",
  DISCARDED = "DISCARDED",
  ALL = "ALL",
}

export const TireStorageListQuerySchema = z.object({
  filter: z
    .nativeEnum(TireStorageListFilter)
    .default(TireStorageListFilter.STORED),
  season: z.nativeEnum(TireSeason).optional(),
  search: z.string().trim().optional(),
})
export type TireStorageListQuery = z.infer<typeof TireStorageListQuerySchema>

export const TireStorageSearchQuerySchema = z.object({
  query: z.string().trim().min(1),
})
export type TireStorageSearchQuery = z.infer<
  typeof TireStorageSearchQuerySchema
>

/** What the create dialog prefills for a customer: price and default end date. */
export const TireStorageDefaultsQuerySchema = z.object({
  storeCustomerId: z.string().uuid(),
})
export type TireStorageDefaultsQuery = z.infer<
  typeof TireStorageDefaultsQuerySchema
>

export interface TireStorageDefaults {
  price: number
  isClubPrice: boolean
  startDate: Date
  endDate: Date
}

export interface TireStorageSettings {
  defaultPrice: number
  clubPrice: number | null
  defaultDurationMonths: number
  warnDaysBeforeEnd: number
}

export const SaveTireStorageSettingsSchema = z.object({
  defaultPrice: z.number().int().min(0),
  clubPrice: z.number().int().min(0).nullable(),
  defaultDurationMonths: z.number().int().min(1).max(60),
  warnDaysBeforeEnd: z.number().int().min(0).max(365),
})
export type SaveTireStorageSettings = z.infer<
  typeof SaveTireStorageSettingsSchema
>

export interface TireStorageExpiringCount {
  count: number
}

export const TireStorageReminderQuerySchema = z.object({
  season: z.nativeEnum(TireSeason).optional(),
})
export type TireStorageReminderQuery = z.infer<
  typeof TireStorageReminderQuerySchema
>

/** Who the tire-change reminder goes to and the text it starts from. */
export interface TireStorageReminderPreview {
  numRecipients: number
  numRecipientsWithEmail: number
  /** One recipient, for counting the SMS segments the text will cost. */
  representativeStoreCustomerId: string | null
  /** Store variables resolved; customer variables left as tags. */
  defaultContent: string
}

export const SendTireStorageReminderSchema = z.object({
  season: z.nativeEnum(TireSeason).optional(),
  content: z.string().trim().min(1),
  channels: MessageChannelSchema.array().min(1).max(2),
  sendFullSms: z.boolean(),
})
export type SendTireStorageReminder = z.infer<
  typeof SendTireStorageReminderSchema
>

export interface SentTireStorageReminder {
  numSent: number
  numFailed: number
}

/**
 * Start and end dates are calendar days. A date picked in the browser is
 * local midnight, which in Sweden is the previous day in UTC; send the UTC
 * midnight of the picked day so the API stores the day that was picked.
 */
export const toTireStorageDate = (date: Date): Date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))

/** For select inputs, whose values are plain strings. */
export const parseTireSeason = (value: string): TireSeason | null =>
  Object.values(TireSeason).find((season) => season === value) ?? null

export const parseTireStorageQuantity = (
  value: string,
): TireStorageQuantity | null =>
  Object.values(TireStorageQuantity).find((quantity) => quantity === value) ??
  null

export const parseTireStorageListFilter = (
  value: string,
): TireStorageListFilter | null =>
  Object.values(TireStorageListFilter).find((filter) => filter === value) ??
  null
